import clsx from 'clsx';
import Information from 'mdi-material-ui/Information';
import InformationOutline from 'mdi-material-ui/InformationOutline';
import { useState } from 'react';

const variants = {
	light: {
		container: 'bg-primaryBackground text-primaryTextDark border-secondaryBorder',
		pointer: 'bg-primaryBackground border-secondaryBorder',
	},
	dark: {
		container: 'bg-primaryTextDark text-primaryTextLight border-primaryTextDark',
		pointer: 'bg-primaryTextDark border-primaryTextDark',
	},
};

const defaultContainerPosition = {
	top: 'bottom-[40px] right-[-16px] lg:bottom-[36px] lg:right-0 lg:translate-x-[50%]',
	right: 'top-[35px] right-[-16px] lg:top-[12px] lg:left-[45px] lg:right-auto lg:translate-y-[-50%]',
	bottom: 'top-[35px] right-[-16px] lg:top-[12px] lg:top-[32px] lg:right-0 lg:translate-x-[50%]',
	left: 'top-[35px] right-[-16px] lg:top-[12px] lg:right-[40px] lg:translate-y-[-50%]',
};

const defaultPointerPosition = {
	top: 'rotate-[-45deg] right-[22px] bottom-[-6px] ml-[-18px] lg:bottom-[-6px] lg:rotate-[-45deg] lg:left-[50%] lg:ml-[-18px]',
	right: 'rotate-[132deg] right-[22px] top-[-6px] lg:rotate-45 lg:right-auto lg:left-[-6px] lg:top-[50%] lg:mt-[-6px]',
	bottom: 'rotate-[132deg] right-[22px] top-[-6px] lg:rotate-[132deg] lg:right-auto lg:top-[-5px] lg:left-[50%] lg:ml-[-18px]',
	left: 'rotate-[132deg] right-[22px] top-[-6px] lg:rotate-[226deg] lg:right-[-6px] lg:top-[50%] lg:mt-[-6px]',
};

const sizes = {
	small: {
		container: 'w-[125px] p-1 leading-[1.3] text-center text-sm',
		containerPosition: {
			withIcon: {
				...defaultContainerPosition,
				top: 'bottom-[40px] right-[-18px] lg:bottom-[32px] lg:right-[8px] lg:translate-x-[50%]',
			},
			withoutIcon: {
				...defaultContainerPosition,
				top: 'bottom-[52px] right-[10px]',
				right: 'top-[12px] left-[40px] translate-y-[-50%]',
			},
		},
		pointerPosition: {
			withIcon: {
				...defaultPointerPosition,
				top: 'rotate-[-45deg] right-[22px] bottom-[-6px] ml-[-18px] lg:bottom-[-6px] lg:rotate-[-45deg] lg:left-[50%] lg:ml-[-8px]',
			},
			withoutIcon: {
				...defaultPointerPosition,
				top: 'rotate-[-45deg] left-[50%] bottom-[-6px] ml-[-6px]',
				right: 'rotate-45 left-[-6px] top-[50%] mt-[-6px]',
			},
		},
	},
	large: {
		container: 'w-[450px] max-w-sm md:max-w-md p-4',
		containerPosition: {
			withIcon: defaultContainerPosition,
			withoutIcon: defaultContainerPosition,
		},
		pointerPosition: {
			withIcon: defaultContainerPosition,
			withoutIcon: defaultContainerPosition,
		},
	},
};

export type TooltipProps = {
	iconClassName?: string;
	tooltipClassName?: string;
	description: string;
	children: string | string[] | React.ReactElement | React.ReactElement[];
	position?: 'top' | 'right' | 'bottom' | 'left';
	TooltipTriggerElement?: React.ReactElement;
	containerClassName?: string;
	variant?: keyof typeof variants;
	size?: keyof typeof sizes;
	iconSize?: 'small' | 'large';
};

export const Tooltip = (props: TooltipProps): React.ReactNode => {
	const {
		iconClassName,
		tooltipClassName,
		description,
		children,
		position = 'right',
		TooltipTriggerElement,
		containerClassName = '',
		variant = 'light',
		size = 'large',
		iconSize = 'medium',
	} = props;
	const [hideTooltip, setHideTooltip] = useState(true);

	return (
		<div className={`relative max-w-md flex ${containerClassName}`}>
			<button
				className={clsx('flex peer', iconClassName)}
				aria-describedby='tooltip-message'
				onClick={() => {
					setHideTooltip(!hideTooltip);
				}}
				type='button'
			>
				{TooltipTriggerElement ? (
					TooltipTriggerElement
				) : (
					<>
						<span className='sr-only'>{description}</span>
						{variant === 'light' ? (
							<InformationOutline
								aria-hidden='true'
								fontSize={iconSize}
								className='text-secondaryTextDark'
							/>
						) : (
							<Information
								aria-hidden='true'
								fontSize={iconSize}
								className='text-primaryTextDark'
							/>
						)}
					</>
				)}
			</button>
			<div
				className={`flex absolute z-50 peer-hover:!visible max-w-[80vw]
					${sizes[size].containerPosition[!!TooltipTriggerElement ? 'withoutIcon' : 'withIcon'][position]}
					${hideTooltip ? 'invisible' : ''}
				`}
			>
				<div
					tabIndex={-1}
					className={clsx(
						'block border-2 rounded-[4px] relative z-1',
						variants[variant].container,
						sizes[size].container,
						tooltipClassName
					)}
					id='tooltip-message'
					aria-hidden='true'
					role='tooltip'
				>
					{children}
				</div>
				<span
					className={clsx(
						`absolute w-[12px] h-[12px] border-2 border-t-0 border-r-0 z-0`,
						sizes[size].pointerPosition[
							!!TooltipTriggerElement ? 'withoutIcon' : 'withIcon'
						][position],
						variants[variant].pointer
					)}
				></span>
			</div>
		</div>
	);
};
